import { X, Search, Maximize, Trash2, Volume2, Send } from "lucide-react";
import { useState, useEffect } from "react";

// Sentence starters — child taps one to seed the sentence with a friendly opener.
// Optional: a kid can skip these and just build from cards.
const SENTENCE_STARTERS = [
  { id: 'want', text: 'I want', face: '🙋' },
  { id: 'feel', text: 'I feel', face: '💛' },
  { id: 'need', text: 'I need', face: '🆘' },
  { id: 'can-we', text: 'Can we', face: '🤝' },
  { id: 'dont-like', text: "I don't like", face: '🙅' },
];

const PICTURE_CARDS = {
  feelings: {
    label: 'Feelings',
    color: 'bg-[#FFF7D6]',
    activeColor: 'bg-[#FCEB9C]',
    textColor: 'text-[#B89823]',
    cards: [
      { id: 'f-happy', face: '😊', text: 'I feel happy', label: 'Happy' },
      { id: 'f-sad', face: '😢', text: 'I feel sad', label: 'Sad' },
      { id: 'f-angry', face: '😠', text: 'I feel angry', label: 'Angry' },
      { id: 'f-scared', face: '😨', text: 'I feel scared', label: 'Scared' },
      { id: 'f-tired', face: '😴', text: 'I feel tired', label: 'Tired' },
      { id: 'f-confused', face: '😕', text: 'I feel confused', label: 'Confused' },
      { id: 'f-excited', face: '🤩', text: 'I feel excited', label: 'Excited' },
      { id: 'f-worried', face: '😟', text: 'I feel worried', label: 'Worried' },
      { id: 'f-calm', face: '😌', text: 'I feel calm', label: 'Calm' },
      { id: 'f-proud', face: '🥳', text: 'I feel proud', label: 'Proud' },
      { id: 'f-shy', face: '🫣', text: 'I feel shy', label: 'Shy' },
      { id: 'f-bored', face: '😒', text: 'I feel bored', label: 'Bored' },
      { id: 'f-frustrated', face: '😤', text: 'I feel frustrated', label: 'Frustrated' },
    ]
  },
  needs: {
    label: 'I Need...',
    color: 'bg-[#E6F0F9]',
    activeColor: 'bg-[#C9DFE9]',
    textColor: 'text-[#4A7AA5]',
    cards: [
      { id: 'n-help', face: '🆘', text: 'I need help', label: 'Help' },
      { id: 'n-mum', face: '👩', text: 'I want my mum', label: 'Want Mum' },
      { id: 'n-dad', face: '👨', text: 'I want my dad', label: 'Want Dad' },
      { id: 'n-home', face: '🏠', text: 'I want to go home', label: 'Go home' },
      { id: 'n-drink', face: '🥤', text: 'I need a drink', label: 'Drink' },
      { id: 'n-food', face: '🍎', text: 'I am hungry', label: 'Food' },
      { id: 'n-toilet', face: '🚻', text: 'I need the toilet', label: 'Toilet' },
      { id: 'n-break', face: '⏸️', text: 'I need a break', label: 'Break' },
      { id: 'n-hug', face: '🤗', text: 'I need a hug', label: 'Hug' },
      { id: 'n-medicine', face: '💊', text: 'I need medicine', label: 'Medicine' },
      { id: 'n-bag', face: '🎒', text: 'I need my bag', label: 'My bag' },
      { id: 'n-coat', face: '🧥', text: 'I need my coat', label: 'My coat' },
      { id: 'n-quiet', face: '🤫', text: 'I need quiet', label: 'Quiet' },
      { id: 'n-sleep', face: '😪', text: 'I need to sleep', label: 'Sleep' },
      { id: 'n-toy', face: '🧸', text: 'I need my toy', label: 'Toy' },
      { id: 'n-car', face: '🚗', text: 'I need my car', label: 'Car' },
      { id: 'n-dolly', face: '🐰', text: 'I need my dolly', label: 'Dolly' },
      { id: 'n-dog', face: '🐕', text: 'I need my dog', label: 'Dog' },
      { id: 'n-cat', face: '🐈', text: 'I need my cat', label: 'Cat' },
      { id: 'n-shoes', face: '👟', text: 'I need my shoes', label: 'My shoes' },
      { id: 'n-clothes', face: '👚', text: 'I need my clothes', label: 'My clothes' },
      { id: 'n-uniform', face: '🎽', text: 'I need my uniform', label: 'My uniform' },
    ]
  },
  school: {
    label: 'At School',
    color: 'bg-[#E8F5E9]',
    activeColor: 'bg-[#D0E8D0]',
    textColor: 'text-[#5A8A5A]',
    cards: [
      { id: 's-dontunderstand', face: '❓', text: "I don't understand", label: "Don't understand" },
      { id: 's-loud', face: '🔊', text: 'It is too loud', label: 'Too loud' },
      { id: 's-hard', face: '😣', text: 'This is too hard', label: 'Too hard' },
      { id: 's-finished', face: '✅', text: 'I have finished', label: 'Finished' },
      { id: 's-moretime', face: '⏰', text: 'I need more time', label: 'More time' },
      { id: 's-readtome', face: '📖', text: 'Please read to me', label: 'Read to me' },
      { id: 's-lost', face: '😶', text: 'I am lost', label: "I'm lost" },
      { id: 's-cantsee', face: '👀', text: "I can't see", label: "Can't see" },
      { id: 's-canthear', face: '👂', text: "I can't hear", label: "Can't hear" },
      { id: 's-want', face: '🙋', text: 'I want to answer', label: 'Pick me' },
      { id: 's-bully', face: '😰', text: 'Someone is being mean to me', label: 'Being bullied' },
      { id: 's-taken', face: '😢', text: 'Someone took my things', label: 'Took my things' },
      { id: 's-easy', face: '👌', text: 'This is too easy', label: 'Too easy' },
      { id: 's-playwithme', face: '🤝', text: 'Play with me', label: 'Play with me' },
      { id: 's-sitwithme', face: '🪑', text: 'Sit with me', label: 'Sit with me' },
      { id: 's-quietroom', face: '🤫', text: 'I need the quiet room', label: 'Quiet Room' },
      { id: 's-sensory', face: '🌈', text: 'I need the sensory room', label: 'Sensory Room' },
    ]
  },
  talking: {
    label: 'Talking',
    color: 'bg-[#F3E8FF]',
    activeColor: 'bg-[#E8D5FF]',
    textColor: 'text-[#7C5AAA]',
    cards: [
      { id: 't-play', face: '🎮', text: 'Play with me', label: 'Play with me' },
      { id: 't-alone', face: '🚶', text: 'Leave me alone please', label: 'Leave me alone' },
      { id: 't-sorry', face: '🙏', text: 'I am sorry', label: 'Sorry' },
      { id: 't-thankyou', face: '💛', text: 'Thank you', label: 'Thank you' },
      { id: 't-myturn', face: '☝️', text: 'It is my turn', label: 'My turn' },
      { id: 't-yes', face: '👍', text: 'Yes', label: 'Yes' },
      { id: 't-no', face: '👎', text: 'No', label: 'No' },
      { id: 't-dontknow', face: '🤷', text: "I don't know", label: "Don't know" },
      { id: 't-stop', face: '✋', text: 'Please stop', label: 'Stop' },
      { id: 't-friend', face: '🤝', text: 'I want a friend', label: 'Want a friend' },
      { id: 't-share', face: '🫴', text: 'I want to share', label: 'Share' },
      { id: 't-wait', face: '⏳', text: 'Please wait for me', label: 'Wait for me' },
      { id: 't-listen', face: '👂', text: 'Please listen to me', label: 'Listen to me' },
    ]
  },
  body: {
    label: 'My Body',
    color: 'bg-[#FCEAEA]',
    activeColor: 'bg-[#F3C4C4]',
    textColor: 'text-[#A55B64]',
    cards: [
      { id: 'b-tummy', face: '🤢', text: 'My tummy hurts', label: 'Tummy hurts' },
      { id: 'b-head', face: '🤕', text: 'My head hurts', label: 'Head hurts' },
      { id: 'b-sick', face: '🤒', text: 'I feel sick', label: 'Feel sick' },
      { id: 'b-hot', face: '🥵', text: 'I am too hot', label: 'Too hot' },
      { id: 'b-cold', face: '🥶', text: 'I am too cold', label: 'Too cold' },
      { id: 'b-breathe', face: '😮‍💨', text: "I can't breathe well", label: "Can't breathe" },
      { id: 'b-hurt', face: '🩹', text: 'I hurt myself', label: 'Hurt myself' },
      { id: 'b-dizzy', face: '😵', text: 'I feel dizzy', label: 'Dizzy' },
      { id: 'b-cry', face: '😭', text: 'I need to cry', label: 'Need to cry' },
      { id: 'b-teeth', face: '🦷', text: 'My teeth hurt', label: 'Teeth hurt' },
      { id: 'b-itchy', face: '🤧', text: 'I feel itchy', label: 'Itchy' },
      { id: 'b-leg', face: '🦵', text: 'My leg hurts', label: 'Leg hurts' },
      { id: 'b-arm', face: '💪', text: 'My arm hurts', label: 'Arm hurts' },
      { id: 'b-fingers', face: '🖐️', text: 'My fingers hurt', label: 'Fingers hurt' },
      { id: 'b-bleeding', face: '🩸', text: 'I am bleeding', label: 'Bleeding' },
    ]
  },
  people: {
    label: 'People',
    color: 'bg-[#FFF0E6]',
    activeColor: 'bg-[#FFD9BE]',
    textColor: 'text-[#C47A3A]',
    cards: [
      { id: 'p-mum', face: '👩', text: 'I want to see my mum', label: 'Mum' },
      { id: 'p-dad', face: '👨', text: 'I want to see my dad', label: 'Dad' },
      { id: 'p-teacher', face: '👩‍🏫', text: 'I want to see my teacher', label: 'Teacher' },
      { id: 'p-friend', face: '👦', text: 'I want to see my friend', label: 'Friend' },
      { id: 'p-brother', face: '👦', text: 'I want my brother', label: 'Brother' },
      { id: 'p-sister', face: '👧', text: 'I want my sister', label: 'Sister' },
      { id: 'p-grandma', face: '👵', text: 'I want my grandma', label: 'Grandma' },
      { id: 'p-grandpa', face: '👴', text: 'I want my grandpa', label: 'Grandpa' },
      { id: 'p-helper', face: '🧑‍⚕️', text: 'I need a helper', label: 'Helper' },
      { id: 'p-doctor', face: '👨‍⚕️', text: 'I need a doctor', label: 'Doctor' },
      { id: 'p-carer', face: '🤱', text: 'I want my carer', label: 'Carer' },
    ]
  },
  activities: {
    label: 'Activities',
    color: 'bg-[#E6F9F0]',
    activeColor: 'bg-[#C9F0DE]',
    textColor: 'text-[#3A8A6A]',
    cards: [
      { id: 'a-draw', face: '🎨', text: 'I want to draw', label: 'Drawing' },
      { id: 'a-read', face: '📚', text: 'I want to read', label: 'Reading' },
      { id: 'a-play', face: '🧸', text: 'I want to play with toys', label: 'Playing' },
      { id: 'a-sing', face: '🎤', text: 'I want to sing', label: 'Singing' },
      { id: 'a-walk', face: '🚶', text: 'I want to go for a walk', label: 'Walking' },
      { id: 'a-rest', face: '🛋️', text: 'I want to rest', label: 'Resting' },
      { id: 'a-watch', face: '📺', text: 'I want to watch something', label: 'Watching' },
      { id: 'a-listen', face: '🎧', text: 'I want to listen to music', label: 'Listening' },
      { id: 'a-write', face: '✏️', text: 'I want to write', label: 'Writing' },
      { id: 'a-build', face: '🧱', text: 'I want to build something', label: 'Building' },
      { id: 'a-cook', face: '🍳', text: 'I want to cook', label: 'Cooking' },
      { id: 'a-dance', face: '💃', text: 'I want to dance', label: 'Dancing' },
      { id: 'a-bedtime', face: '🛏️', text: 'It is bedtime', label: 'Bedtime' },
      { id: 'a-food', face: '🍽️', text: 'It is foodtime', label: 'Foodtime' },
      { id: 'a-shopping', face: '🛍️', text: 'I want to go shopping', label: 'Shopping' },
      { id: 'a-outside', face: '🌿', text: 'I want to play outside', label: 'Playing Outside' },
      { id: 'a-change', face: '👕', text: 'I need to change clothes', label: 'Change Clothes' },
      { id: 'a-bath', face: '🛁', text: 'It is bathtime', label: 'Bathtime' },
      { id: 'a-board', face: '🎲', text: 'I want to play board games', label: 'Board Games' },
      { id: 'a-sport', face: '⚽', text: 'I want to do sport', label: 'Sport' },
      { id: 'a-swim', face: '🏊', text: 'I want to go swimming', label: 'Swimming' },
      { id: 'a-ride', face: '🚲', text: 'I want to go riding', label: 'Riding' },
    ]
  },
  places: {
    label: 'Places',
    color: 'bg-[#E6E9F9]',
    activeColor: 'bg-[#C9CEE9]',
    textColor: 'text-[#4A5AA5]',
    cards: [
      { id: 'pl-home', face: '🏠', text: 'I want to go home', label: 'Home' },
      { id: 'pl-class', face: '🏫', text: 'I want to go to class', label: 'Classroom' },
      { id: 'pl-playground', face: '🛝', text: 'I want to go to the playground', label: 'Playground' },
      { id: 'pl-toilet', face: '🚻', text: 'I need to go to the toilet', label: 'Toilet' },
      { id: 'pl-library', face: '📚', text: 'I want to go to the library', label: 'Library' },
      { id: 'pl-hall', face: '🏛️', text: 'I want to go to the hall', label: 'Hall' },
      { id: 'pl-office', face: '🏢', text: 'I need to go to the office', label: 'Office' },
      { id: 'pl-outside', face: '🌳', text: 'I want to go outside', label: 'Outside' },
      { id: 'pl-car', face: '🚗', text: 'I want to go to the car', label: 'Car' },
      { id: 'pl-shop', face: '🛒', text: 'I want to go to the shop', label: 'Shop' },
      { id: 'pl-garden', face: '🌻', text: 'I want to go to the garden', label: 'Garden' },
    ]
  },
  safety: {
    label: 'My Safety',
    color: 'bg-[#FDE8E8]',
    activeColor: 'bg-[#F5C2C2]',
    textColor: 'text-[#B44D4D]',
    cards: [
      { id: 's-hungry', face: '🍽️', text: "I haven't eaten", label: "Haven't eaten" },
      { id: 's-wash', face: '🚿', text: 'I need a wash', label: 'Need a wash' },
      { id: 's-dirty', face: '👕', text: 'I need clean clothes', label: 'Need clean clothes' },
      { id: 's-nosleep', face: '😴', text: "I didn't sleep", label: "Didn't sleep" },
      { id: 's-nohelphurt', face: '🤕', text: 'Nobody helped me when I was hurt', label: 'No help when hurt' },
      { id: 's-unsafe', face: '😶', text: "I don't feel safe at home", label: 'Unsafe at home' },
      { id: 's-neglect', face: '😢', text: 'Nobody looks after me', label: 'Nobody looks after me' },
      { id: 's-secret', face: '🤫', text: 'Someone told me not to tell', label: 'Told not to tell' },
      { id: 's-hurts', face: '😨', text: 'Someone hurts me', label: 'Someone hurts me' },
      { id: 's-nohome', face: '🏠', text: "I don't want to go home", label: "Don't want to go home" },
    ]
  },
  weather: {
    label: 'Weather',
    color: 'bg-[#E0F2FE]',
    activeColor: 'bg-[#BAE6FD]',
    textColor: 'text-[#0369A1]',
    cards: [
      { id: 'w-sunny', face: '☀️', text: 'It is sunny', label: 'Sunny' },
      { id: 'w-cloudy', face: '☁️', text: 'It is cloudy', label: 'Cloudy' },
      { id: 'w-rainy', face: '🌧️', text: 'It is rainy', label: 'Rainy' },
      { id: 'w-snowy', face: '❄️', text: 'It is snowing', label: 'Snowy' },
      { id: 'w-windy', face: '💨', text: 'It is windy', label: 'Windy' },
      { id: 'w-stormy', face: '⛈️', text: 'There is a storm', label: 'Stormy' },
      { id: 'w-hot', face: '🌡️', text: 'It is hot today', label: 'Hot' },
      { id: 'w-cold', face: '🧊', text: 'It is cold today', label: 'Cold' },
      { id: 'w-rainbow', face: '🌈', text: 'There is a rainbow', label: 'Rainbow' },
      { id: 'w-foggy', face: '🌫️', text: 'It is foggy', label: 'Foggy' },
    ]
  },
  days: {
    label: 'Days',
    color: 'bg-[#FEF3C7]',
    activeColor: 'bg-[#FDE68A]',
    textColor: 'text-[#92400E]',
    cards: [
      { id: 'd-mon', face: '1️⃣', text: 'Today is Monday', label: 'Monday' },
      { id: 'd-tue', face: '2️⃣', text: 'Today is Tuesday', label: 'Tuesday' },
      { id: 'd-wed', face: '3️⃣', text: 'Today is Wednesday', label: 'Wednesday' },
      { id: 'd-thu', face: '4️⃣', text: 'Today is Thursday', label: 'Thursday' },
      { id: 'd-fri', face: '5️⃣', text: 'Today is Friday', label: 'Friday' },
      { id: 'd-sat', face: '6️⃣', text: 'Today is Saturday', label: 'Saturday' },
      { id: 'd-sun', face: '7️⃣', text: 'Today is Sunday', label: 'Sunday' },
      { id: 'd-today', face: '📅', text: 'What day is it today?', label: 'What day?' },
      { id: 'd-tomorrow', face: '➡️', text: 'What is happening tomorrow?', label: 'Tomorrow' },
      { id: 'd-yesterday', face: '⬅️', text: 'What happened yesterday?', label: 'Yesterday' },
      { id: 'd-weekend', face: '🎉', text: 'It is the weekend', label: 'Weekend' },
    ]
  },
  time: {
    label: 'Time',
    color: 'bg-[#F3E8FF]',
    activeColor: 'bg-[#DDD6FE]',
    textColor: 'text-[#6D28D9]',
    cards: [
      { id: 'tm-morning', face: '🌅', text: 'It is morning', label: 'Morning' },
      { id: 'tm-afternoon', face: '🌤️', text: 'It is afternoon', label: 'Afternoon' },
      { id: 'tm-evening', face: '🌇', text: 'It is evening', label: 'Evening' },
      { id: 'tm-night', face: '🌙', text: 'It is night time', label: 'Night' },
      { id: 'tm-now', face: '⏰', text: 'What time is it now?', label: 'What time?' },
      { id: 'tm-soon', face: '🔜', text: 'Is it soon?', label: 'Soon?' },
      { id: 'tm-wait', face: '⏳', text: 'How long do I wait?', label: 'How long?' },
      { id: 'tm-late', face: '😰', text: 'I am late', label: 'Late' },
      { id: 'tm-early', face: '🌄', text: 'It is too early', label: 'Too early' },
      { id: 'tm-lunch', face: '🍽️', text: 'Is it lunchtime?', label: 'Lunchtime?' },
      { id: 'tm-breakfast', face: '🥣', text: 'Is it breakfast time?', label: 'Breakfast?' },
      { id: 'tm-dinner', face: '🍲', text: 'Is it dinner time?', label: 'Dinner?' },
      { id: 'tm-tea', face: '☕', text: 'Is it tea time?', label: 'Tea time?' },
      { id: 'tm-playtime', face: '🎯', text: 'Is it playtime?', label: 'Playtime?' },
      { id: 'tm-hometime', face: '🏠', text: 'Is it home time?', label: 'Home time?' },
    ]
  },
  months: {
    label: 'Months',
    color: 'bg-[#E0F2E9]',
    activeColor: 'bg-[#A7F3D0]',
    textColor: 'text-[#065F46]',
    cards: [
      { id: 'm-jan', face: '❄️', text: 'It is January', label: 'January' },
      { id: 'm-feb', face: '💝', text: 'It is February', label: 'February' },
      { id: 'm-mar', face: '🌱', text: 'It is March', label: 'March' },
      { id: 'm-apr', face: '🌷', text: 'It is April', label: 'April' },
      { id: 'm-may', face: '🌸', text: 'It is May', label: 'May' },
      { id: 'm-jun', face: '☀️', text: 'It is June', label: 'June' },
      { id: 'm-jul', face: '🏖️', text: 'It is July', label: 'July' },
      { id: 'm-aug', face: '🌻', text: 'It is August', label: 'August' },
      { id: 'm-sep', face: '🍂', text: 'It is September', label: 'September' },
      { id: 'm-oct', face: '🎃', text: 'It is October', label: 'October' },
      { id: 'm-nov', face: '🍁', text: 'It is November', label: 'November' },
      { id: 'm-dec', face: '🎄', text: 'It is December', label: 'December' },
      { id: 'm-spring', face: '🌼', text: 'It is spring', label: 'Spring' },
      { id: 'm-summer', face: '🌞', text: 'It is summer', label: 'Summer' },
      { id: 'm-autumn', face: '🍃', text: 'It is autumn', label: 'Autumn' },
      { id: 'm-winter', face: '⛄', text: 'It is winter', label: 'Winter' },
    ]
  }
};

const PictureCardsModal = ({ isOpen, onClose, onSendCardMessage, onCategoryOpen }) => {
  const [activeCategory, setActiveCategory] = useState('feelings');
  // 'cards' = tap to send single card (original AAC behaviour)
  // 'build' = tap to add card to a sentence; SEND when ready
  const [mode, setMode] = useState('cards');
  // Sentence builder state — array of {kind:'starter'|'card', face, label, text, id}
  const [sentence, setSentence] = useState([]);

  // Record the default category whenever the modal opens (kid often just
  // explores "feelings" without tapping another tab — that's still useful
  // research signal for SEND profiling).
  useEffect(() => {
    if (isOpen && onCategoryOpen) onCategoryOpen(activeCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  // Reset sentence when modal closes so the next open starts fresh
  useEffect(() => {
    if (!isOpen) {
      setSentence([]);
      setMode('cards');
    }
  }, [isOpen]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showToTeacher, setShowToTeacher] = useState(null);
  const [showSentenceFullscreen, setShowSentenceFullscreen] = useState(false);

  if (!isOpen && !showToTeacher) return null;

  // Show the assembled sentence at full-screen size so the child can show
  // it to a teacher, parent or grown-up across the room. Same UX pattern
  // as the per-card "Show to adult" overlay.
  if (showSentenceFullscreen && sentence.length > 0) {
    return (
      <div
        className="fixed inset-0 bg-white z-[90] flex flex-col items-center justify-center p-6 cursor-pointer"
        onClick={() => setShowSentenceFullscreen(false)}
        data-testid="sentence-fullscreen"
      >
        <div className="text-center w-full max-w-5xl">
          <div className="flex flex-wrap gap-3 sm:gap-4 justify-center items-center mb-8">
            {sentence.map((c, i) => (
              <span
                key={c.id + i}
                className={`inline-flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-4 rounded-3xl text-3xl sm:text-5xl font-bold ${
                  c.kind === 'starter'
                    ? 'bg-[#FCEAEA] text-[#A55B64] border-2 border-[#F3C4C4]'
                    : 'bg-[#E6F0F9] text-[#4A7AA5] border-2 border-[#C9DFE9]'
                }`}
              >
                <span className="text-4xl sm:text-6xl">{c.face}</span>
                <span>{c.label}</span>
              </span>
            ))}
          </div>
          <p className="text-base sm:text-lg text-[#9A9A9A] mt-8">Tap anywhere to go back</p>
        </div>
      </div>
    );
  }

  if (showToTeacher) {
    return (
      <div 
        className="fixed inset-0 bg-white z-[70] flex flex-col items-center justify-center p-8 cursor-pointer"
        onClick={() => setShowToTeacher(null)}
        data-testid="show-to-teacher-screen"
      >
        <div className="text-center">
          <span className="text-[120px] sm:text-[180px] block mb-6">{showToTeacher.face}</span>
          <p className="text-4xl sm:text-6xl font-bold text-[#4A4A4A] mb-4">{showToTeacher.text}</p>
          <p className="text-lg text-[#9A9A9A] mt-8">Tap anywhere to go back</p>
        </div>
      </div>
    );
  }

  const categories = Object.entries(PICTURE_CARDS);

  const getFilteredCards = () => {
    if (!searchQuery.trim()) {
      return PICTURE_CARDS[activeCategory].cards;
    }
    const query = searchQuery.toLowerCase();
    const allCards = [];
    categories.forEach(([, cat]) => {
      cat.cards.forEach(card => {
        if (card.label.toLowerCase().includes(query) || card.text.toLowerCase().includes(query)) {
          allCards.push(card);
        }
      });
    });
    return allCards;
  };

  const filteredCards = getFilteredCards();

  // ----- Sentence Builder helpers -----
  // Pick a starter — replaces any existing starter (only one allowed, at start).
  const pickStarter = (starter) => {
    setSentence((prev) => {
      const withoutStarter = prev.filter((c) => c.kind !== 'starter');
      return [{ kind: 'starter', id: `starter-${starter.id}`, face: starter.face, label: starter.text, text: starter.text }, ...withoutStarter];
    });
  };

  const addCardToSentence = (card) => {
    setSentence((prev) => [...prev, { kind: 'card', id: `${card.id}-${prev.length}`, face: card.face, label: card.label, text: card.label }]);
  };

  const removeFromSentence = (idx) => {
    setSentence((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearSentence = () => setSentence([]);

  // Compose the final string for sending. Starter keeps its case (e.g. "I want"),
  // card labels are lowercased and joined with spaces — natural enough for a
  // poodle to understand, gentle on grammar for SEND kids.
  const buildSentenceText = () => {
    if (sentence.length === 0) return '';
    const starter = sentence.find((c) => c.kind === 'starter');
    const cards = sentence.filter((c) => c.kind === 'card');
    const parts = [];
    if (starter) parts.push(starter.text);
    cards.forEach((c, i) => {
      // First card after starter stays as-is, the rest lowercase for flow
      parts.push(i === 0 && !starter ? c.label : c.label.toLowerCase());
    });
    return parts.join(' ').trim();
  };

  const previewSentence = () => {
    const text = buildSentenceText();
    if (!text) return;
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 0.95;
      u.pitch = 1.1;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch (_e) { /* speech blocked, silently ignore */ }
  };

  const sendSentence = () => {
    const text = buildSentenceText();
    if (!text) return;
    // Visual chip representation for the chat bubble — face + label
    const visual = sentence.map((c) => (c.face ? `${c.face} ${c.label}` : c.label)).join(' ');
    onSendCardMessage(visual, 'sentence_builder');
    setSentence([]);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[80] p-2 sm:p-4" onClick={onClose} data-testid="picture-cards-modal">
      <div className="bg-[#FDFBF7] rounded-3xl w-full max-w-2xl shadow-2xl relative flex flex-col" style={{ maxHeight: '90vh' }} onClick={(e) => e.stopPropagation()}>
        
        <div className="p-4 sm:p-5 border-b border-[#E8E5DC] flex-shrink-0">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-xl sm:text-2xl font-bold text-[#4A4A4A]">Picture Cards</h2>
            <button onClick={onClose} className="p-1 text-[#6B6B6B] hover:text-[#4A4A4A] transition-colors" data-testid="picture-cards-close">
              <X className="w-7 h-7" />
            </button>
          </div>

          {/* Mode toggle: 🃏 Cards | 🗣️ Build */}
          <div className="flex gap-2 mb-3" data-testid="card-mode-toggle">
            <button
              type="button"
              onClick={() => setMode('cards')}
              data-testid="card-mode-cards"
              className={`flex-1 py-2 px-3 rounded-full text-sm font-semibold transition-all ${
                mode === 'cards'
                  ? 'bg-[#FCEB9C] text-[#B89823] shadow-sm'
                  : 'bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC]'
              }`}
            >
              🃏 Cards
            </button>
            <button
              type="button"
              onClick={() => setMode('build')}
              data-testid="card-mode-build"
              className={`flex-1 py-2 px-3 rounded-full text-sm font-semibold transition-all ${
                mode === 'build'
                  ? 'bg-[#C9DFE9] text-[#4A7AA5] shadow-sm'
                  : 'bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC]'
              }`}
            >
              🗣️ Build a sentence
            </button>
          </div>

          {/* In Build mode show the sentence strip + starter chips */}
          {mode === 'build' && (
            <div className="mb-3" data-testid="sentence-builder-strip">
              <div className="bg-white border-2 border-dashed border-[#C9DFE9] rounded-2xl p-3 min-h-[64px] flex flex-wrap gap-2 items-center">
                {sentence.length === 0 ? (
                  <p className="text-sm text-[#9A9A9A] w-full text-center">
                    Tap a starter below, then tap picture cards to build your sentence ✨
                  </p>
                ) : (
                  sentence.map((c, i) => (
                    <span
                      key={c.id + i}
                      data-testid={`sentence-chip-${i}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-sm font-medium ${
                        c.kind === 'starter'
                          ? 'bg-[#FCEAEA] text-[#A55B64] border border-[#F3C4C4]'
                          : 'bg-[#E6F0F9] text-[#4A7AA5] border border-[#C9DFE9]'
                      }`}
                    >
                      <span className="text-base">{c.face}</span>
                      <span>{c.label}</span>
                      <button
                        type="button"
                        onClick={() => removeFromSentence(i)}
                        data-testid={`sentence-chip-remove-${i}`}
                        className="ml-1 text-[#9A9A9A] hover:text-[#A55B64]"
                        aria-label="Remove"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))
                )}
              </div>

              {/* Starter chips */}
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="sentence-starters">
                {SENTENCE_STARTERS.map((s) => {
                  const active = sentence[0]?.kind === 'starter' && sentence[0]?.id === `starter-${s.id}`;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => pickStarter(s)}
                      data-testid={`sentence-starter-${s.id}`}
                      className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium border transition-all ${
                        active
                          ? 'bg-[#FCEAEA] text-[#A55B64] border-[#F3C4C4]'
                          : 'bg-white text-[#6B6B6B] border-[#EAE3D2] hover:bg-[#F8F3E8]'
                      }`}
                    >
                      <span className="mr-1">{s.face}</span>
                      {s.text}…
                    </button>
                  );
                })}
                {sentence.length > 0 && (
                  <button
                    type="button"
                    onClick={clearSentence}
                    data-testid="sentence-clear"
                    className="px-3 py-1 rounded-full text-xs sm:text-sm font-medium text-[#9A9A9A] hover:text-[#A55B64] inline-flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Clear
                  </button>
                )}
              </div>
            </div>
          )}

          {mode === 'cards' && (
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9A9A9A]" />
              <input
                type="text"
                placeholder="Search cards..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="picture-cards-search"
                className="w-full pl-9 pr-4 py-2 bg-[#F3F0E6] rounded-full text-sm text-[#4A4A4A] placeholder-[#9A9A9A] outline-none focus:ring-2 focus:ring-[#C9DFE9]"
              />
            </div>
          )}

          {(mode === 'build' || !searchQuery) && (
            <div className="flex flex-wrap gap-1.5">
              {categories.map(([key, cat]) => (
                <button
                  key={key}
                  onClick={() => { setActiveCategory(key); if (onCategoryOpen) onCategoryOpen(key); }}
                  data-testid={`card-category-${key}`}
                  className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition-all ${
                    activeCategory === key
                      ? `${cat.activeColor} ${cat.textColor} font-semibold`
                      : 'bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC]'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 sm:p-5 overflow-y-auto flex-1">
          {mode === 'cards' && searchQuery && (
            <p className="text-xs text-[#9A9A9A] mb-3">{filteredCards.length} card{filteredCards.length !== 1 ? 's' : ''} found</p>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3">
            {(mode === 'build' ? PICTURE_CARDS[activeCategory].cards : filteredCards).map(card => (
              <div key={card.id} className="group relative">
                <button
                  onClick={() => {
                    if (mode === 'build') {
                      addCardToSentence(card);
                    } else {
                      onSendCardMessage(`${card.face} ${card.text}`, card.id);
                      onClose();
                    }
                  }}
                  data-testid={`picture-card-${card.id}`}
                  className="w-full flex flex-col items-center gap-1 p-2.5 sm:p-3 rounded-2xl bg-white border-2 border-[#E8E5DC] hover:border-[#C9DFE9] hover:shadow-md transition-all active:scale-95"
                >
                  <span className="text-3xl sm:text-4xl">{card.face}</span>
                  <span className="text-[10px] sm:text-xs font-medium text-[#4A4A4A] text-center leading-tight">{card.label}</span>
                </button>
                {mode === 'cards' && (
                  <button
                    onClick={() => setShowToTeacher(card)}
                    className="absolute -top-1 -right-1 w-6 h-6 bg-[#4A7AA5] text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity shadow-sm"
                    title="Show to adult"
                  >
                    <Maximize className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {mode === 'build' ? (
          <div className="p-3 border-t border-[#E8E5DC] flex-shrink-0 flex items-center gap-2">
            <button
              type="button"
              onClick={previewSentence}
              disabled={sentence.length === 0}
              data-testid="sentence-preview-button"
              className="px-3 py-2 rounded-full bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-xs sm:text-sm inline-flex items-center gap-1.5"
              title="Hear it first"
            >
              <Volume2 className="w-4 h-4" /> Listen
            </button>
            <button
              type="button"
              onClick={() => setShowSentenceFullscreen(true)}
              disabled={sentence.length === 0}
              data-testid="sentence-fullscreen-button"
              className="p-2 rounded-full bg-[#F3F0E6] text-[#6B6B6B] hover:bg-[#E8E5DC] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              title="Show big to a grown-up"
              aria-label="Show sentence full screen"
            >
              <Maximize className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={sendSentence}
              disabled={sentence.length === 0}
              data-testid="sentence-send-button"
              className="flex-1 py-2.5 rounded-full bg-[#A55B64] hover:bg-[#8A4951] text-white font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" /> Send to my poodle
            </button>
          </div>
        ) : (
          <div className="p-3 border-t border-[#E8E5DC] flex-shrink-0 text-center">
            <p className="text-[10px] sm:text-xs text-[#9A9A9A]">
              Tap a card to send • Hover and tap <Maximize className="w-3 h-3 inline" /> to show full screen to adults
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PictureCardsModal;
